
import React, { useState, useEffect, useContext } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Users, Filter, Search, Plus, X, AlertCircle, Check, RefreshCw, Smartphone, Mail, Shield, Trash2, Receipt, UserPlus } from "lucide-react";
import { useAuth } from "../../context/AuthContext";
import { NotificationContext } from "../../context/NotificationContext";
import { staffAPI } from "../../api/apiService";
import { Link, useNavigate } from "react-router-dom";
import EmptyState from "../../components/EmptyState";

const getRoleLabel = (roles) => {
    if (!roles || roles.length === 0) return 'Sales Executive';
    const roleName = roles[0].name;
    return roleName.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
};

export default function AllEmployees() {
    const { user, refreshProfile } = useAuth();
    const { addNotification } = useContext(NotificationContext);
    const [staff, setStaff] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchQuery, setSearchQuery] = useState("");
    const [staffToDelete, setStaffToDelete] = useState(null);
    const navigate = useNavigate();

    const fetchStaff = () => {
        setLoading(true);
        staffAPI.getAllStaff()
            .then(res => setStaff(res.data))
            .catch(console.error)
            .finally(() => setLoading(false));
    };

    useEffect(() => {
        refreshProfile();
        fetchStaff();
    }, []);

    const toggleStatus = async (id, status) => {
        const newStatus = !status;
        try {
            await staffAPI.updateStaff(id, { is_active: newStatus });
            setStaff(prev => prev.map(u => u.id === id ? { ...u, is_active: newStatus } : u));
            addNotification(`Employee ${newStatus ? 'activated' : 'disabled'} successfully`, "success");
        } catch (err) {
            addNotification("Failed to update status", "error");
        }
    };

    const handleDeleteConfirm = async () => {
        if (!staffToDelete) return;
        try {
            await staffAPI.deleteStaff(staffToDelete.id);
            addNotification("Employee deleted successfully", "success");
            fetchStaff();
        } catch (err) {
            addNotification("Failed to delete employee", "error");
        } finally {
            setStaffToDelete(null);
        }
    };

    const filteredStaff = staff.filter(member => 
        `${member.first_name} ${member.last_name}`.toLowerCase().includes(searchQuery.toLowerCase()) ||
        member.email?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        member.salesman_id?.toLowerCase().includes(searchQuery.toLowerCase())
    );

    return (
        <div className="min-h-screen bg-gray-50 dark:bg-slate-900 p-4 md:p-6">
            <div className="max-w-7xl mx-auto space-y-6">
                
                {/* Header */}
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 bg-white dark:bg-slate-800 p-6 rounded-2xl shadow-sm border border-gray-100 dark:border-slate-700">
                    <div className="flex items-center gap-4">
                        <div className="p-3 bg-green-600 rounded-xl text-white shadow-lg shadow-green-500/30">
                            <Users className="w-8 h-8" />
                        </div>
                        <div>
                            <h1 className="text-2xl font-black text-gray-900 dark:text-white">All Employees</h1>
                            <p className="text-gray-500 dark:text-slate-400 font-medium">Manage and monitor your team performance</p>
                        </div>
                    </div>
                    <button
                        onClick={() => navigate('/employees/add')}
                        className="flex items-center justify-center gap-2 px-6 py-3 bg-green-600 text-white rounded-xl font-bold hover:bg-green-700 transition-all shadow-lg shadow-green-500/20 active:scale-95"
                    >
                        <UserPlus className="w-5 h-5" />
                        Add Employee
                    </button>
                </div>

                {/* Stats Summary */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <QuickStat label="Total Team" value={staff.length} icon={Users} color="blue" />
                    <QuickStat label="Active" value={staff.filter(s => s.is_active).count} icon={Check} color="green" />
                    <QuickStat label="Bills Today" value={staff.reduce((acc, s) => acc + (s.today_bills || 0), 0)} icon={Receipt} color="purple" />
                    <QuickStat label="New Customers Today" value={staff.reduce((acc, s) => acc + (s.today_customers || 0), 0)} icon={UserPlus} color="orange" />
                </div>

                {/* Search & Filter */}
                <div className="bg-white dark:bg-slate-800 p-4 rounded-2xl shadow-sm border border-gray-100 dark:border-slate-700 flex flex-col md:flex-row gap-4">
                    <div className="relative flex-1">
                        <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                        <input 
                            type="text"
                            placeholder="Search by name, email or ID..."
                            className="w-full pl-12 pr-4 py-3 bg-gray-50 dark:bg-slate-900 border-none rounded-xl focus:ring-2 focus:ring-green-500 transition-all dark:text-white"
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                        />
                    </div>
                </div>

                {/* Employees Table */}
                <div className="bg-white dark:bg-slate-800 rounded-2xl shadow-sm border border-gray-100 dark:border-slate-700 overflow-hidden">
                    {loading ? (
                        <EmptyState
                            preset="people"
                            title="Loading employee records..."
                            message="Please wait a moment while we load your team members."
                            className="py-12"
                        />
                    ) : (
                        <div className="overflow-x-auto">
                            <table className="w-full text-left">
                                <thead className="bg-gray-50 dark:bg-slate-900/50 border-b border-gray-100 dark:border-slate-700">
                                    <tr>
                                        <th className="px-6 py-4 text-xs font-bold uppercase text-gray-400">Employee</th>
                                        <th className="px-6 py-4 text-xs font-bold uppercase text-gray-400">Role & Status</th>
                                        <th className="px-6 py-4 text-xs font-bold uppercase text-gray-400">Bills (Total/Today)</th>
                                        <th className="px-6 py-4 text-xs font-bold uppercase text-gray-400">Customers (Total/Today)</th>
                                        <th className="px-6 py-4 text-xs font-bold uppercase text-gray-400 text-right">Actions</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-gray-100 dark:divide-slate-700">
                                    {filteredStaff.length === 0 ? (
                                        <tr>
                                            <td colSpan="5" className="px-6 py-12 text-center">
                                                <EmptyState
                                                    preset="people"
                                                    title="No employees found"
                                                    message="No employee records matching the search criteria."
                                                    className="py-6"
                                                />
                                            </td>
                                        </tr>
                                    ) : filteredStaff.map(member => (
                                        <tr key={member.id} className="hover:bg-gray-50 dark:hover:bg-slate-700/30 transition-colors">
                                            <td className="px-6 py-4">
                                                <div className="flex items-center gap-3">
                                                    <div className="w-10 h-10 rounded-full bg-green-100 dark:bg-green-900/30 flex items-center justify-center text-green-700 dark:text-green-400 font-bold">
                                                        {member.first_name?.[0]}{member.last_name?.[0]}
                                                    </div>
                                                    <div>
                                                        <p className="font-bold text-gray-900 dark:text-white">{member.first_name} {member.last_name}</p>
                                                        <p className="text-xs text-gray-500">ID: {member.salesman_id || 'N/A'}</p>
                                                        {member.store_details && (
                                                            <p className="text-[10px] text-green-600 dark:text-green-400 font-bold mt-1">
                                                                🏬 {member.store_details.name}
                                                            </p>
                                                        )}
                                                    </div>
                                                </div>
                                            </td>
                                            <td className="px-6 py-4">
                                                <div className="space-y-2">
                                                    <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-blue-50 text-blue-700 dark:bg-blue-900/20 dark:text-blue-300 text-[10px] font-black uppercase tracking-wider">
                                                        <Shield className="w-3 h-3" />
                                                        {getRoleLabel(member.roles)}
                                                    </span>
                                                    <button 
                                                        onClick={() => toggleStatus(member.id, member.is_active)}
                                                        className={`block text-[10px] font-bold px-2.5 py-1 rounded-full border transition-all ${
                                                            member.is_active 
                                                                ? "bg-green-50 text-green-600 border-green-100" 
                                                                : "bg-gray-50 text-gray-400 border-gray-100"
                                                        }`}
                                                    >
                                                        {member.is_active ? '● Active' : '○ Disabled'}
                                                    </button>
                                                </div>
                                            </td>
                                            <td className="px-6 py-4">
                                                <div className="flex items-end gap-2">
                                                    <p className="text-lg font-black text-gray-900 dark:text-white">{member.total_bills || 0}</p>
                                                    <p className="text-xs text-green-600 font-bold mb-1">+{member.today_bills || 0} today</p>
                                                </div>
                                            </td>
                                            <td className="px-6 py-4">
                                                <div className="flex items-end gap-2">
                                                    <p className="text-lg font-black text-gray-900 dark:text-white">{member.total_customers || 0}</p>
                                                    <p className="text-xs text-blue-600 font-bold mb-1">+{member.today_customers || 0} today</p>
                                                </div>
                                            </td>
                                            <td className="px-6 py-4 text-right">
                                                <div className="flex items-center justify-end gap-2">
                                                    <button 
                                                        onClick={() => navigate(`/employees/edit/${member.id}`, { state: { member } })}
                                                        className="p-2 text-gray-400 hover:text-green-600 hover:bg-green-50 rounded-lg transition-all"
                                                        title="Edit Details"
                                                    >
                                                        <Filter className="w-5 h-5" />
                                                    </button>
                                                    <button 
                                                        onClick={() => setStaffToDelete({ id: member.id, name: `${member.first_name} ${member.last_name}` })}
                                                        className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-all"
                                                        title="Delete Employee"
                                                    >
                                                        <Trash2 className="w-5 h-5" />
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>
            </div>

            {/* Delete Confirmation Modal */}
            {staffToDelete && (
                <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={() => setStaffToDelete(null)}>
                    <div className="bg-white dark:bg-slate-800 rounded-xl p-8 max-w-md w-full shadow-2xl" onClick={e => e.stopPropagation()}>
                        <div className="flex flex-col items-center text-center">
                            <div className="w-16 h-16 rounded-full bg-red-50 dark:bg-red-950/30 flex items-center justify-center text-red-600 dark:text-red-400 mb-6 animate-pulse">
                                <Trash2 className="w-8 h-8" />
                            </div>
                            <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-3">Delete Employee</h3>
                            <p className="text-gray-600 dark:text-gray-300 text-sm mb-8 leading-relaxed">
                                Are you sure you want to delete <span className="font-bold text-gray-900 dark:text-white">"{staffToDelete.name}"</span>? This action cannot be undone.
                            </p>
                            <div className="flex gap-4 w-full">
                                <button
                                    onClick={() => setStaffToDelete(null)}
                                    className="flex-1 py-3 rounded-lg border border-gray-300 dark:border-slate-700 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-slate-700 font-bold transition-colors"
                                >
                                    Cancel
                                </button>
                                <button
                                    onClick={handleDeleteConfirm}
                                    className="flex-1 py-3 rounded-lg bg-red-600 hover:bg-red-700 text-white font-bold transition-colors"
                                >
                                    Delete
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

function QuickStat({ label, value, icon: Icon, color }) {
    const colors = {
        blue: "bg-blue-50 text-blue-600 dark:bg-blue-900/20 dark:text-blue-400",
        green: "bg-green-50 text-green-600 dark:bg-green-900/20 dark:text-green-400",
        purple: "bg-purple-50 text-purple-600 dark:bg-purple-900/20 dark:text-purple-400",
        orange: "bg-orange-50 text-orange-600 dark:bg-orange-900/20 dark:text-orange-400",
    };

    return (
        <div className="bg-white dark:bg-slate-800 p-4 rounded-2xl border border-gray-100 dark:border-slate-700 shadow-sm flex items-center gap-4">
            <div className={`p-3 rounded-xl ${colors[color]}`}>
                <Icon className="w-6 h-6" />
            </div>
            <div>
                <p className="text-xs font-bold text-gray-400 uppercase tracking-wider">{label}</p>
                <p className="text-xl font-black text-gray-900 dark:text-white">{value}</p>
            </div>
        </div>
    );
}
